/**
 * Classic "wiki document syntax" support.
 *
 * NIP-54 defines wikilink behavior in terms of Djot's native reference-style
 * links: an unresolved `[Text][]` or `[Text][Ref]` link falls back to
 * linking to the wiki article whose (normalized) `d` tag matches `Ref`
 * (or `Text`, if `Ref` is empty). That's precise, but it's not what most
 * people expect to type — nearly every wiki system in the wild (MediaWiki /
 * Wikipedia, Obsidian, TiddlyWiki, Roam, Logseq, ...) uses double-bracket
 * `[[Wikilink]]` syntax instead.
 *
 * To support that familiar convention without inventing a new content
 * format, Wikistar accepts double-bracket wikilinks as an *input* syntax and
 * expands them into spec-compliant Djot reference links before the text is
 * parsed or published:
 *
 *   [[Bitcoin]]           -> [Bitcoin][]            (displays "Bitcoin", links to d-tag "bitcoin")
 *   [[Bitcoin|the coin]]  -> [the coin][Bitcoin]     (displays "the coin", links to d-tag "bitcoin")
 *
 * This expansion is applied in two places:
 *  - At render time (`parseDjot`, used by both the live editor preview and
 *    the published-article view), so `[[...]]` wikilinks always render as
 *    working links — including in content written by hand or with tools
 *    that don't know about this convenience syntax.
 *  - At publish time (see `ArticleEditPage`), so the `content` field that
 *    actually gets signed and sent to relays is fully spec-compliant NIP-54
 *    Djot — portable to any Nostr wiki client, not just Wikistar.
 *
 * Fenced code blocks and inline verbatim spans are left untouched, so
 * writing about wikilink syntax (e.g. in an article explaining Wikistar's
 * own conventions) doesn't get mangled.
 */

const FENCE_START_RE = /^\s*(`{3,})/;
/** Approximates Djot inline verbatim spans (`` `code` ``) well enough to avoid rewriting inside them. */
const INLINE_VERBATIM_RE = /`[^`\n]*`/g;
const WIKILINK_RE = /\[\[([^[\]|\n]+)(?:\|([^[\]\n]+))?\]\]/g;

/**
 * Returns true if `position` (a character offset into `content`) falls
 * inside a fenced code block (` ``` `). Used to suppress wikilink expansion
 * and autocomplete triggering inside code samples.
 */
export function isPositionInFence(content: string, position: number): boolean {
  const lines = content.slice(0, position).split('\n');
  let fenceLen = 0;
  for (const line of lines) {
    const match = line.match(FENCE_START_RE);
    if (fenceLen > 0) {
      if (match && match[1].length >= fenceLen) fenceLen = 0;
      continue;
    }
    if (match) fenceLen = match[1].length;
  }
  return fenceLen > 0;
}

function expandWikilinksInText(text: string): string {
  return text.replace(WIKILINK_RE, (_match, target: string, display?: string) => {
    const t = target.trim();
    if (!t) return _match;
    if (display !== undefined) {
      const d = display.trim();
      return `[${d || t}][${t}]`;
    }
    return `[${t}][]`;
  });
}

/** Expands `[[...]]` wikilinks in a single line, skipping over inline verbatim spans. */
function expandWikilinksInLine(line: string): string {
  const parts = line.split(INLINE_VERBATIM_RE);
  const verbatimMatches = line.match(INLINE_VERBATIM_RE) ?? [];
  return parts
    .map((part, i) => expandWikilinksInText(part) + (verbatimMatches[i] ?? ''))
    .join('');
}

/**
 * Expands `[[Target]]` / `[[Target|Display]]` wikilinks into NIP-54-
 * compliant Djot reference links (`[Target][]` / `[Display][Target]`),
 * skipping fenced code blocks entirely.
 */
export function expandWikiLinks(content: string): string {
  if (!content.includes('[[')) return content;

  const lines = content.split('\n');
  const out: string[] = [];
  let fenceLen = 0;

  for (const line of lines) {
    const match = line.match(FENCE_START_RE);
    if (fenceLen > 0) {
      out.push(line);
      if (match && match[1].length >= fenceLen) fenceLen = 0;
      continue;
    }
    if (match) {
      fenceLen = match[1].length;
      out.push(line);
      continue;
    }
    out.push(expandWikilinksInLine(line));
  }

  return out.join('\n');
}
